import type { Coding } from '@medplum/fhirtypes'
import { z } from 'zod'
import { LOINC_CODE_SYSTEM } from '#src/codeSystems.ts'
import type { CodingAnswerOption } from '#src/codings.ts'
import type { ObservationCategoryCode } from '#src/observation-logic/derivations/rules.ts'
import {
  instrumentTemplate,
  INSTRUMENT_TEMPLATES,
} from '#src/terminology/instruments/registry.ts'
import { totalScore } from '#src/terminology/instruments/templateParts.ts'
import type {
  InstrumentItem,
  InstrumentSection,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'

// LOINC panels resolved at runtime from the NLM Clinical Tables form
// definitions (the data LHC-Forms renders), for panels with no instrument in
// source. Medplum's LOINC carries no panel membership, so NLM is the only
// source of a panel's members.
//
// A panel is resolved once, where it is placed (usually in a template), and
// a trimmed copy of the definition is kept on the node. Every note made from
// it rebuilds the same instrument from that copy, without calling NLM.

export const NLM_CLINICAL_TABLES_URL = 'https://clinicaltables.nlm.nih.gov'

const loincPanelAnswerSchema = z.object({
  code: z.string(),
  system: z.string().optional().nullable(),
  display: z.string(),
  score: z.number().optional().nullable(),
})

type LoincPanelAnswer = z.infer<typeof loincPanelAnswerSchema>

export interface LoincPanelItem {
  code: string
  system?: string | null
  label: string
  dataType?: string | null
  unit?: string | null
  answers?: z.infer<typeof loincPanelAnswerSchema>[] | null
  multiple?: boolean | null
  required?: boolean | null
  total?: boolean | null
  min?: number | null
  max?: number | null
  items?: LoincPanelItem[] | null
}

const loincPanelItemSchema: z.ZodType<LoincPanelItem> = z.lazy(() =>
  z.object({
    code: z.string(),
    system: z.string().optional().nullable(),
    label: z.string(),
    dataType: z.string().optional().nullable(),
    unit: z.string().optional().nullable(),
    answers: z.array(loincPanelAnswerSchema).optional().nullable(),
    multiple: z.boolean().optional().nullable(),
    required: z.boolean().optional().nullable(),
    total: z.boolean().optional().nullable(),
    min: z.number().optional().nullable(),
    max: z.number().optional().nullable(),
    items: z.array(loincPanelItemSchema).optional().nullable(),
  })
)

export const loincPanelDefinitionSchema = z.object({
  code: z.string(),
  name: z.string(),
  version: z.string(),
  // From the panel's LOINC class, read where it is placed.
  category: z
    .enum(['vital-signs', 'laboratory', 'survey', 'exam'])
    .optional()
    .nullable(),
  copyright: z.string().optional().nullable(),
  instructions: z.string().optional().nullable(),
  items: z.array(loincPanelItemSchema),
})

export type LoincPanelDefinition = z.infer<typeof loincPanelDefinitionSchema>

// Why a panel cannot be recorded as NLM publishes it.
export type LoincPanelGap =
  // Only the total is published: the questions are copyrighted or were
  // never released to LOINC.
  | { kind: 'score-only' }
  | { kind: 'no-items' }
  // A required question of a kind Orbit cannot capture in a panel yet.
  | { kind: 'unsupported-item'; label: string; dataType: string }

export interface LoincPanelResolution {
  template: InstrumentTemplate | undefined
  // Set when the panel cannot be recorded; the template is then undefined.
  gaps: LoincPanelGap[]
  // Optional questions left out because they cannot be captured yet.
  omitted: string[]
}

/* -------------------------------------------------------------------------
 * NLM payload
 * ---------------------------------------------------------------------- */

interface LformsItem {
  questionCode?: string
  questionCodeSystem?: string | null
  question?: string
  dataType?: string | null
  header?: boolean | null
  units?: { code?: string | null; name?: string | null }[] | null
  answers?:
    | {
        code?: string | null
        text?: string | null
        score?: number | null
        system?: string | null
      }[]
    | null
  items?: LformsItem[] | null
  answerCardinality?: { max?: string | null } | null
  questionCardinality?: { min?: string | null } | null
  restrictions?: Record<string, string> | null
  calculationMethod?: { name?: string | null } | null
}

export interface LformsDefinition {
  code?: string
  name?: string
  lformsVersion?: string | null
  copyrightNotice?: string | null
  codingInstructions?: string | null
  items?: LformsItem[] | null
}

// NLM writes some absent values as the string "null".
function present(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed !== 'null' ? trimmed : undefined
}

function restriction(
  restrictions: Record<string, string> | null | undefined,
  key: string
): number | undefined {
  const value = restrictions?.[key]
  const number = value === undefined ? Number.NaN : Number(value)
  return Number.isFinite(number) ? number : undefined
}

function trimItem(
  item: LformsItem,
  longNames: ReadonlyMap<string, string>
): LoincPanelItem | undefined {
  const code = present(item.questionCode)
  if (!code) {
    return undefined
  }
  const system = present(item.questionCodeSystem ?? undefined)
  const loinc = !system || system === LOINC_CODE_SYSTEM
  const children = (item.items ?? []).flatMap((child) => {
    const trimmed = trimItem(child, longNames)
    return trimmed ? [trimmed] : []
  })
  const unit = present(item.units?.[0]?.code ?? item.units?.[0]?.name)
  const answers = (item.answers ?? []).flatMap((answer) => {
    const answerCode = present(answer.code)
    if (!answerCode) {
      return []
    }
    const trimmedAnswer: LoincPanelAnswer = {
      code: answerCode,
      display: present(answer.text) ?? answerCode,
    }
    const answerSystem = present(answer.system)
    if (answerSystem) {
      trimmedAnswer.system = answerSystem
    }
    if (typeof answer.score === 'number') {
      trimmedAnswer.score = answer.score
    }
    return [trimmedAnswer]
  })
  const min = restriction(item.restrictions, 'minInclusive')
  const max = restriction(item.restrictions, 'maxInclusive')
  const maxAnswers = item.answerCardinality?.max
  const trimmedItem: LoincPanelItem = {
    code,
    // A question with answers reads best in NLM's wording; a measurement
    // in LOINC's long name.
    label:
      (loinc && !answers.length ? longNames.get(code) : undefined) ??
      present(item.question) ??
      code,
  }
  if (system && !loinc) {
    trimmedItem.system = system
  }
  const dataType = present(item.dataType)
  if (dataType) {
    trimmedItem.dataType = dataType
  }
  if (unit) {
    trimmedItem.unit = unit
  }
  if (answers.length) {
    trimmedItem.answers = answers
  }
  if (maxAnswers && maxAnswers !== '1') {
    trimmedItem.multiple = true
  }
  if (item.questionCardinality?.min === '1') {
    trimmedItem.required = true
  }
  if (item.calculationMethod?.name === 'TOTALSCORE') {
    trimmedItem.total = true
  }
  if (min !== undefined) {
    trimmedItem.min = min
  }
  if (max !== undefined) {
    trimmedItem.max = max
  }
  if (item.header || children.length) {
    trimmedItem.items = children
  }
  return trimmedItem
}

function itemCodes(items: readonly LformsItem[]): string[] {
  return items.flatMap((item) => [
    ...(item.questionCode ? [item.questionCode] : []),
    ...itemCodes(item.items ?? []),
  ])
}

// The NLM form definition as kept on a node: only what the instrument is
// built from, with each LOINC measurement named by its long common name
// rather than NLM's terse label ("Systolic blood pressure--supine", not "BP
// sys--sup").
export function loincPanelFromLforms(
  form: LformsDefinition,
  longNames: ReadonlyMap<string, string> = new Map()
): LoincPanelDefinition | undefined {
  const code = present(form.code)
  if (!code) {
    return undefined
  }
  const copyright = present(form.copyrightNotice)
  const instructions = present(form.codingInstructions)
  const definition: LoincPanelDefinition = {
    code,
    name: present(form.name) ?? code,
    version: present(form.lformsVersion) ?? 'unknown',
    items: (form.items ?? []).flatMap((item) => {
      const trimmed = trimItem(item, longNames)
      return trimmed ? [trimmed] : []
    }),
  }
  if (copyright) {
    definition.copyright = copyright
  }
  if (instructions) {
    definition.instructions = instructions
  }
  return definition
}

type FetchLike = (
  url: string,
  init?: { signal?: AbortSignal }
) => Promise<Pick<Response, 'ok' | 'status' | 'json'>>

async function fetchLongNames(
  codes: string[],
  fetcher: FetchLike,
  baseUrl: string,
  signal?: AbortSignal
): Promise<Map<string, string>> {
  if (!codes.length) {
    return new Map()
  }
  const params = new URLSearchParams({
    terms: '',
    q: `LOINC_NUM:(${codes.join(' OR ')})`,
    df: 'LOINC_NUM,LONG_COMMON_NAME',
    maxList: String(Math.min(codes.length, 500)),
  })
  const response = await fetcher(
    `${baseUrl}/api/loinc_items/v3/search?${params}`,
    { signal }
  )
  if (!response.ok) {
    return new Map()
  }
  const body = (await response.json()) as [unknown, unknown, unknown, unknown]
  const rows = Array.isArray(body?.[3]) ? (body[3] as unknown[]) : []
  return new Map(
    rows.flatMap((row) =>
      Array.isArray(row) &&
      typeof row[0] === 'string' &&
      typeof row[1] === 'string'
        ? [[row[0], row[1]] as const]
        : []
    )
  )
}

// A LOINC panel's definition from NLM. Undefined when NLM has no form for
// the code (it is not a panel, or NLM does not carry it). Only LOINC codes
// are sent; nothing about a participant.
export async function fetchLoincPanel(
  code: string,
  {
    fetch: fetcher = fetch,
    signal,
    baseUrl = NLM_CLINICAL_TABLES_URL,
  }: { fetch?: FetchLike; signal?: AbortSignal; baseUrl?: string } = {}
): Promise<LoincPanelDefinition | undefined> {
  const response = await fetcher(
    `${baseUrl}/loinc_form_definitions?loinc_num=${encodeURIComponent(code)}`,
    { signal }
  )
  if (response.status === 404) {
    return undefined
  }
  if (!response.ok) {
    throw new Error(`NLM panel lookup failed (${response.status})`)
  }
  const form = (await response.json()) as LformsDefinition & {
    Error?: string
  }
  if (form.Error || !form.code) {
    return undefined
  }
  // Long names are a nicety; a failed lookup keeps NLM's labels.
  const longNames = await fetchLongNames(
    [...new Set(itemCodes(form.items ?? []))],
    fetcher,
    baseUrl,
    signal
  ).catch(() => new Map<string, string>())
  return loincPanelFromLforms(form, longNames)
}

// LOINC panels matching a search, as NLM ranks them. Medplum's `$expand`
// returns the shortest names first, so a search such as "blood pressure"
// fills its page with single measurements before reaching any panel.
export async function searchLoincPanels(
  terms: string,
  {
    fetch: fetcher = fetch,
    signal,
    baseUrl = NLM_CLINICAL_TABLES_URL,
    limit = 4,
  }: {
    fetch?: FetchLike
    signal?: AbortSignal
    baseUrl?: string
    limit?: number
  } = {}
): Promise<Coding[]> {
  if (!terms.trim()) {
    return []
  }
  const params = new URLSearchParams({
    terms,
    type: 'form',
    df: 'LOINC_NUM,LONG_COMMON_NAME',
    maxList: String(limit),
  })
  const response = await fetcher(
    `${baseUrl}/api/loinc_items/v3/search?${params}`,
    { signal }
  )
  if (!response.ok) {
    throw new Error(`NLM panel search failed (${response.status})`)
  }
  const body = (await response.json()) as unknown[]
  const rows = Array.isArray(body?.[3]) ? (body[3] as unknown[]) : []
  return rows.flatMap((row) =>
    Array.isArray(row) &&
    typeof row[0] === 'string' &&
    typeof row[1] === 'string'
      ? [{ system: LOINC_CODE_SYSTEM, code: row[0], display: row[1] }]
      : []
  )
}

/* -------------------------------------------------------------------------
 * Instrument
 * ---------------------------------------------------------------------- */

// The category a LOINC panel's records take, from its LOINC CLASS and
// CLASSTYPE (1 laboratory, 2 clinical, 4 survey). Blood pressure, heart rate
// and vital signs panels are vital signs; other clinical panels are exams.
export function loincPanelCategory(
  loincClass: string | undefined,
  classType: string | undefined
): ObservationCategoryCode | undefined {
  if (!loincClass && !classType) {
    return undefined
  }
  if (
    /^PANEL\.(VITALS|BP|HRTRATE|BDYTMP|BDYWGT|BDYHGT|PULSE)\b/.test(
      loincClass ?? ''
    )
  ) {
    return 'vital-signs'
  }
  if (classType === '1') {
    return 'laboratory'
  }
  if (classType === '4' || loincClass?.startsWith('PANEL.SURVEY')) {
    return 'survey'
  }
  return 'exam'
}

export function loincPanelTemplateId(code: string): string {
  return `loinc-panel-${code}`
}

const NUMBER_TYPES = new Set(['REAL', 'INT', 'QTY', 'NR'])
const TEXT_TYPES = new Set(['ST', 'TX'])
const CHOICE_TYPES = new Set(['CNE', 'CWE'])
// Display-only rows.
const SKIPPED_TYPES = new Set(['TITLE', 'SECTION'])

function answerOption(
  answer: NonNullable<LoincPanelItem['answers']>[number]
): CodingAnswerOption {
  const option: CodingAnswerOption = {
    code: answer.code,
    display: answer.display,
    system: answer.system ?? LOINC_CODE_SYSTEM,
  }
  if (typeof answer.score === 'number') {
    option.score = answer.score
  }
  return option
}

// The instrument item a question is captured as, or why it cannot be.
function instrumentItemFor(
  item: LoincPanelItem
): InstrumentItem | { unsupported: string } | undefined {
  const dataType = item.dataType ?? ''
  if (SKIPPED_TYPES.has(dataType)) {
    return undefined
  }
  const coding = {
    system: item.system ?? LOINC_CODE_SYSTEM,
    code: item.code,
    display: item.label,
  }
  const base = {
    coding,
    label: item.label,
    required: item.required === true,
  }
  if (NUMBER_TYPES.has(dataType) && !item.answers?.length) {
    const numberItem: InstrumentItem = {
      ...base,
      scaleType: 'Qn',
      scored: false,
    }
    if (item.unit) {
      numberItem.unit = item.unit
    }
    if (
      item.min !== undefined &&
      item.min !== null &&
      item.max !== undefined &&
      item.max !== null
    ) {
      numberItem.range = { min: item.min, max: item.max }
    }
    return numberItem
  }
  if (TEXT_TYPES.has(dataType)) {
    return { ...base, scaleType: 'Nar', scored: false }
  }
  if (
    (CHOICE_TYPES.has(dataType) || item.answers?.length) &&
    item.answers?.length &&
    !item.multiple
  ) {
    const scored = item.answers.some(
      (answer) => typeof answer.score === 'number'
    )
    return {
      ...base,
      scaleType: scored ? 'Ord' : 'Nom',
      answerOptions: item.answers.map(answerOption),
      scored,
    }
  }
  return { unsupported: item.multiple ? `${dataType} (multiple)` : dataType }
}

interface Collected {
  sections: InstrumentSection[]
  totals: LoincPanelItem[]
  gaps: LoincPanelGap[]
  omitted: string[]
}

function collect(definition: LoincPanelDefinition): Collected {
  const collected: Collected = {
    sections: [],
    totals: [],
    gaps: [],
    omitted: [],
  }
  const loose: InstrumentItem[] = []
  const add = (item: LoincPanelItem, into: InstrumentItem[]): void => {
    if (item.items) {
      for (const child of item.items) {
        add(child, into)
      }
      return
    }
    if (item.total) {
      collected.totals.push(item)
      return
    }
    const captured = instrumentItemFor(item)
    if (!captured) {
      return
    }
    if ('unsupported' in captured) {
      if (item.required) {
        collected.gaps.push({
          kind: 'unsupported-item',
          label: item.label,
          dataType: captured.unsupported,
        })
      } else {
        collected.omitted.push(item.label)
      }
      return
    }
    into.push(captured)
  }
  for (const item of definition.items) {
    if (item.items) {
      // A header at the top is a section; deeper headers fold into it.
      const items: InstrumentItem[] = []
      for (const child of item.items) {
        add(child, items)
      }
      if (items.length) {
        collected.sections.push({ id: item.code, title: item.label, items })
      }
    } else {
      add(item, loose)
    }
  }
  if (loose.length) {
    collected.sections.unshift({ id: 'items', items: loose })
  }
  return collected
}

// The instrument a panel is captured as, or the reasons it cannot be: NLM
// carries only the total of a copyrighted tool (MoCA, MMSE), and some
// question kinds have no panel input yet.
export function instrumentFromLoincPanel(
  definition: LoincPanelDefinition
): LoincPanelResolution {
  const { sections, totals, gaps, omitted } = collect(definition)
  const items = sections.flatMap((section) => section.items)
  const scored = items.some((item) => item.scored)
  if (!items.length) {
    gaps.unshift(totals.length ? { kind: 'score-only' } : { kind: 'no-items' })
  }
  if (gaps.length) {
    return { template: undefined, gaps, omitted }
  }
  const id = loincPanelTemplateId(definition.code)
  const totalItem = scored ? totals[0] : undefined
  const template: InstrumentTemplate = {
    id,
    version: `nlm-${definition.version}`,
    panel: {
      system: LOINC_CODE_SYSTEM,
      code: definition.code,
      display: definition.name,
    },
    title: definition.name,
    category: definition.category ?? (scored ? 'survey' : 'exam'),
    reference: `https://loinc.org/${definition.code}`,
    sections,
  }
  if (definition.copyright) {
    template.copyright = definition.copyright
  }
  if (totalItem) {
    template.total = totalScore({
      templateId: id,
      coding: {
        system: totalItem.system ?? LOINC_CODE_SYSTEM,
        code: totalItem.code,
        display: totalItem.label,
      },
      sections,
      completeness: { requireAll: true },
      // NLM publishes no cutoffs; a tool with bands is ported to source.
      bands: [],
    })
  }
  return { template, gaps: [], omitted }
}

// A short reason a panel cannot be recorded, for the person placing it.
export function loincPanelGapMessage(gap: LoincPanelGap): string {
  switch (gap.kind) {
    case 'score-only':
      return 'LOINC publishes only its total score; the questions are copyrighted or not released.'
    case 'no-items':
      return 'LOINC publishes no questions for it.'
    case 'unsupported-item':
      return `"${gap.label}" is a ${gap.dataType} question, which panels cannot capture yet.`
  }
}

const resolved = new Map<string, InstrumentTemplate | undefined>()

// The instrument a panel node captures: the one in source for its template
// id, else the one built from the NLM definition kept on the node. Built
// templates are kept by definition, since the node view and the note's
// capture ask for them on every render.
export function panelTemplate(
  attrs: { templateId?: unknown; definition?: unknown },
  templates: readonly InstrumentTemplate[] = INSTRUMENT_TEMPLATES
): InstrumentTemplate | undefined {
  const inSource = instrumentTemplate(String(attrs.templateId ?? ''), templates)
  if (inSource || !attrs.definition) {
    return inSource
  }
  const key =
    typeof attrs.definition === 'string'
      ? attrs.definition
      : JSON.stringify(attrs.definition)
  if (!resolved.has(key)) {
    let raw: unknown = attrs.definition
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw)
      } catch {
        raw = undefined
      }
    }
    const parsed = loincPanelDefinitionSchema.safeParse(raw)
    resolved.set(
      key,
      parsed.success
        ? instrumentFromLoincPanel(parsed.data).template
        : undefined
    )
  }
  return resolved.get(key)
}
